import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios'; // Import axios
import PublicHeader from '../Common/Layout/PublicHeader';
import { rexTechTheme } from '../Common/Theme';
import { Mail, Lock, Eye, EyeOff, User, AlertCircle, ArrowRight, Phone } from 'lucide-react';
import config from '../Common/Configurations/APIConfig'; // Import your API config


const Register = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    companyType: 'CLIENT',
    userType: 'CLIENTUSER', // Static role as requested
    isActive: true,

  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState({ type: '', text: '' }); // success or error message

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phoneNumber.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain letters and numbers';
    }
    
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
 
    
    return newErrors;
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setApiMessage({ type: '', text: '' });
  
  const validationErrors = validateForm();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }
  
  setIsLoading(true);
  
  try {
    // Create request data with all required fields
    const requestData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      password: formData.password,
      companyType: 'CLIENT',
      userType: 'CLIENTUSER',
        isActive: true
    };
    
    console.log('Sending payload:', requestData); // Debug log
    
    const response = await axios.post(
      `${config.APIACTIVEURL}${config.CREATECOMPANY}`,
      requestData,
      {
         headers: {
                        'Content-Type': 'multipart/form-data',
                    }
      }
    );
    
    if (response.data.success) {
      setApiMessage({
        type: 'success',
        text: response.data.message || 'Account created successfully!'
      });
      
      if (response.data.userId) {
        localStorage.setItem('userId', response.data.userId);
      }
      
      // Clear form
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
  
        companyType: 'CLIENT',
        userType: 'CLIENTUSER',
        isActive: true
      });
      setErrors({});
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } else {
      setApiMessage({
        type: 'error',
        text: response.data.message || 'Registration failed. Please try again.'
      });
      
      if (response.data.message?.includes('Email already exists')) {
        setErrors(prev => ({ ...prev, email: 'Email already exists' }));
      }
    }
  } catch (error) {
    console.error('Registration error:', error);
    
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error status:', error.response.status);
      
      const errorData = error.response.data;
      setApiMessage({
        type: 'error',
        text: errorData.message || `Registration failed: ${error.response.status}`
      });
      
      if (error.response.status === 400) {
        // Handle validation errors
        if (errorData.errors) {
          const backendErrors = {};
          Object.keys(errorData.errors).forEach(key => {
            backendErrors[key.toLowerCase()] = errorData.errors[key][0];
          });
          setErrors(prev => ({ ...prev, ...backendErrors }));
        }
      }
      
      if (error.response.status === 409) {
        setErrors(prev => ({ ...prev, email: 'Email already exists' }));
      }
    } else if (error.request) {
      setApiMessage({
        type: 'error',
        text: 'Network error. Please check your connection and try again.'
      });
    } else {
      setApiMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.'
      });
    }
  } finally {
    setIsLoading(false);
  }
};
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: rexTechTheme.colors.neutral.lightGray,
      fontFamily: rexTechTheme.typography.fontFamily
    }}>
      {/* Header */}
      <PublicHeader />
      
      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: rexTechTheme.spacing.xl,
        paddingTop: rexTechTheme.spacing.xxl,
        '@media (max-width: 768px)': {
          padding: rexTechTheme.spacing.md,
          paddingTop: rexTechTheme.spacing.xl
        }
      }}>
        <div style={{
          width: '100%',
          maxWidth: '450px',
          backgroundColor: rexTechTheme.colors.neutral.white,
          padding: rexTechTheme.spacing.xxl,
          borderRadius: rexTechTheme.borderRadius.xlarge,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          '@media (max-width: 768px)': {
            padding: rexTechTheme.spacing.xl
          }
        }}>
          {/* Form Header */}
          <div style={{ textAlign: 'center', marginBottom: rexTechTheme.spacing.xl }}>
            <h1 style={{
              margin: `0 0 ${rexTechTheme.spacing.md} 0`,
              fontSize: rexTechTheme.typography.fontSize.xxlarge,
              color: rexTechTheme.colors.primary.dark,
              fontWeight: 700
            }}>
              Create Account
            </h1>
            <p style={{
              margin: 0,
              color: rexTechTheme.colors.neutral.darkGray,
              fontSize: rexTechTheme.typography.fontSize.regular
            }}>
              Join RexTech and start your sustainability journey
            </p>
          </div>

          {/* API Message */}
          {apiMessage.text && (
            <div style={{
              backgroundColor: apiMessage.type === 'success' 
                ? rexTechTheme.colors.primary.lightest 
                : rexTechTheme.colors.accent.lightest,
              border: `1px solid ${apiMessage.type === 'success' 
                ? rexTechTheme.colors.primary.light 
                : rexTechTheme.colors.accent.orange}`,
              color: apiMessage.type === 'success'
                ? rexTechTheme.colors.primary.dark
                : rexTechTheme.colors.accent.orange,
              padding: rexTechTheme.spacing.md,
              borderRadius: rexTechTheme.borderRadius.medium,
              marginBottom: rexTechTheme.spacing.lg,
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.sm,
              fontSize: rexTechTheme.typography.fontSize.small
            }}>
              {apiMessage.type === 'success' ? (
                <AlertCircle size={16} color={rexTechTheme.colors.primary.main} />
              ) : (
                <AlertCircle size={16} color={rexTechTheme.colors.accent.orange} />
              )}
              <span>{apiMessage.text}</span>
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {/* Name Field */}
            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
              <label style={{
                display: 'block',
                marginBottom: rexTechTheme.spacing.xs,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: 600,
                fontSize: rexTechTheme.typography.fontSize.regular
              }}>
                Full Name *
              </label>
              <div style={{ position: 'relative' }}>
                <User 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: rexTechTheme.colors.neutral.darkGray,
                    pointerEvents: 'none'
                  }}
                />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({...formData, name: e.target.value});
                    if (errors.name) setErrors({...errors, name: ''});
                    setApiMessage({ type: '', text: '' });
                  }}
                  style={{
                    width: '100%',
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} 48px`,
                    border: `2px solid ${errors.name ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    ':focus': {
                      borderColor: rexTechTheme.colors.primary.main,
                      boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                    }
                  }}
                  placeholder="Enter your full name"
                  disabled={isLoading}
                />
              </div>
              {errors.name && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.xs,
                  marginTop: rexTechTheme.spacing.xs,
                  color: rexTechTheme.colors.accent.orange,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <AlertCircle size={14} />
                  <span>{errors.name}</span>
                </div>
              )}
            </div>

            {/* Email Field */}
            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
              <label style={{
                display: 'block',
                marginBottom: rexTechTheme.spacing.xs,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: 600,
                fontSize: rexTechTheme.typography.fontSize.regular
              }}>
                Email Address *
              </label>
              <div style={{ position: 'relative' }}>
                <Mail 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: rexTechTheme.colors.neutral.darkGray,
                    pointerEvents: 'none'
                  }}
                />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({...formData, email: e.target.value});
                    if (errors.email) setErrors({...errors, email: ''});
                    setApiMessage({ type: '', text: '' });
                  }}
                  style={{
                    width: '100%',
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} 48px`,
                    border: `2px solid ${errors.email ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    ':focus': {
                      borderColor: rexTechTheme.colors.primary.main,
                      boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                    }
                  }}
                  placeholder="you@example.com"
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.xs,
                  marginTop: rexTechTheme.spacing.xs,
                  color: rexTechTheme.colors.accent.orange,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <AlertCircle size={14} />
                  <span>{errors.email}</span>
                </div>
              )}
            </div>

            {/* Phone Number Field */}
            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
              <label style={{
                display: 'block',
                marginBottom: rexTechTheme.spacing.xs,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: 600,
                fontSize: rexTechTheme.typography.fontSize.regular
              }}>
                Phone Number *
              </label>
              <div style={{ position: 'relative' }}>
                <Phone 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: rexTechTheme.colors.neutral.darkGray,
                    pointerEvents: 'none'
                  }}
                />
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    setFormData({...formData, phoneNumber: e.target.value});
                    if (errors.phoneNumber) setErrors({...errors, phoneNumber: ''});
                    setApiMessage({ type: '', text: '' });
                  }}
                  style={{
                    width: '100%',
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} 48px`,
                    border: `2px solid ${errors.phoneNumber ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    ':focus': {
                      borderColor: rexTechTheme.colors.primary.main,
                      boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                    }
                  }}
                  placeholder="+1 (555) 123-4567"
                  disabled={isLoading}
                />
              </div>
              {errors.phoneNumber && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.xs,
                  marginTop: rexTechTheme.spacing.xs,
                  color: rexTechTheme.colors.accent.orange,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <AlertCircle size={14} />
                  <span>{errors.phoneNumber}</span>
                </div>
              )}
            </div>

            {/* Password Field */}
            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
              <label style={{
                display: 'block',
                marginBottom: rexTechTheme.spacing.xs,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: 600,
                fontSize: rexTechTheme.typography.fontSize.regular
              }}>
                Password *
              </label>
              <div style={{ position: 'relative' }}>
                <Lock 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: rexTechTheme.colors.neutral.darkGray,
                    pointerEvents: 'none'
                  }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({...formData, password: e.target.value});
                    if (errors.password) setErrors({...errors, password: ''});
                    setApiMessage({ type: '', text: '' });
                  }}
                  style={{
                    width: '100%',
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} 48px`,
                    border: `2px solid ${errors.password ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    ':focus': {
                      borderColor: rexTechTheme.colors.primary.main,
                      boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                    }
                  }}
                  placeholder="Create a password"
                  disabled={isLoading}
                />
                {/* <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: rexTechTheme.colors.neutral.darkGray,
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ':hover': {
                      color: rexTechTheme.colors.primary.main
                    }
                  }}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button> */}
              </div>
              {errors.password && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.xs,
                  marginTop: rexTechTheme.spacing.xs,
                  color: rexTechTheme.colors.accent.orange,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <AlertCircle size={14} />
                  <span>{errors.password}</span>
                </div>
              )}
              <p style={{
                margin: `${rexTechTheme.spacing.xs} 0 0`,
                fontSize: rexTechTheme.typography.fontSize.small,
                color: rexTechTheme.colors.neutral.darkGray
              }}>
                Must be at least 8 characters with letters and numbers
              </p>
            </div>

            {/* Confirm Password Field */}
            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
              <label style={{
                display: 'block',
                marginBottom: rexTechTheme.spacing.xs,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: 600,
                fontSize: rexTechTheme.typography.fontSize.regular
              }}>
                Confirm Password *
              </label>
              <div style={{ position: 'relative' }}>
                <Lock 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: rexTechTheme.colors.neutral.darkGray,
                    pointerEvents: 'none'
                  }}
                />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData({...formData, confirmPassword: e.target.value});
                    if (errors.confirmPassword) setErrors({...errors, confirmPassword: ''});
                    setApiMessage({ type: '', text: '' });
                  }}
                  style={{
                    width: '100%',
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} 48px`,
                    border: `2px solid ${errors.confirmPassword ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    ':focus': {
                      borderColor: rexTechTheme.colors.primary.main,
                      boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                    }
                  }}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                />
                {/* <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: rexTechTheme.colors.neutral.darkGray,
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ':hover': {
                      color: rexTechTheme.colors.primary.main
                    }
                  }}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button> */}
              </div>
              {errors.confirmPassword && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.xs,
                  marginTop: rexTechTheme.spacing.xs,
                  color: rexTechTheme.colors.accent.orange,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <AlertCircle size={14} />
                  <span>{errors.confirmPassword}</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: rexTechTheme.spacing.lg,
                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                color: rexTechTheme.colors.neutral.white,
                border: 'none',
                borderRadius: rexTechTheme.borderRadius.medium,
                fontSize: rexTechTheme.typography.fontSize.regular,
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: rexTechTheme.spacing.sm,
                opacity: isLoading ? 0.7 : 1,
                boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                marginBottom: rexTechTheme.spacing.lg,
                ':hover': {
                  background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                  transform: isLoading ? 'none' : 'translateY(-2px)',
                  boxShadow: isLoading ? '0 4px 15px rgba(30, 132, 73, 0.3)' : '0 6px 20px rgba(30, 132, 73, 0.4)'
                }
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                  Creating Account...
                </>
              ) : (
                <>
                  Create Account
                  {/* <ArrowRight size={18} /> */}
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div style={{
            textAlign: 'center',
            padding: rexTechTheme.spacing.lg,
            backgroundColor: rexTechTheme.colors.primary.lightest,
            borderRadius: rexTechTheme.borderRadius.medium,
            border: `1px solid ${rexTechTheme.colors.primary.lighter}`,
            marginTop: rexTechTheme.spacing.xl
          }}>
            <p style={{
              margin: 0,
              color: rexTechTheme.colors.primary.dark,
              fontSize: rexTechTheme.typography.fontSize.regular
            }}>
              Already have an account?{' '}
              <Link to="/login" style={{
                color: rexTechTheme.colors.primary.main,
                textDecoration: 'none',
                fontWeight: 600,
                ':hover': {
                  textDecoration: 'underline'
                }
              }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        backgroundColor: rexTechTheme.colors.neutral.white,
        padding: rexTechTheme.spacing.lg,
        borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`,
        textAlign: 'center',
        marginTop: 'auto'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          color: rexTechTheme.colors.neutral.darkGray,
          fontSize: rexTechTheme.typography.fontSize.small
        }}>
          <p style={{ margin: 0 }}>
            © 2024 RexTech. Creating a new home for our planet through sustainable technology.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: rexTechTheme.spacing.lg,
            marginTop: rexTechTheme.spacing.sm,
            flexWrap: 'wrap'
          }}>
            <Link 
              to="/privacy" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}>
              Terms of Service
            </Link>
            <Link 
              to="/contact" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Contact Support
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Register;